import {Component, Inject, OnInit} from '@angular/core';
import {L10N_LOCALE, L10nLocale} from "angular-l10n";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {lastValueFrom, Observable, startWith} from "rxjs";
import {map} from "rxjs/operators";
import {
    CompanyCreateReq,
    ContactPersonCreateReq,
    ContactPersonDetailRes,
    NoteCreateReq,
} from '../../../core/contracts/v0/crm';
import {Commtype, Salutation} from '../../../core/enums';
import {RestApiService} from '../../../core/services/rest-api.service';
import {RuntimeCacheService} from '../../../core/services/runtime-cache.service';
import {FileContainerReq, FileUploadRes, getModularValidator, PatternType} from '@yasc/yasc-material-components';
import {CompanyDetailsRes} from '../../../core/contracts/v0/cp';

declare type CheckStatus = "" | "add" | "check"

export class ContactGrouping {
    company!: string;
    contacts!: Map<string, ContactPersonDetailRes>;
}

export class CompanyGrouping {
    name!: string;
    company!: CompanyDetailsRes;

    constructor(comp: CompanyDetailsRes) {
        this.name = comp.name;
        this.company = comp;
    }
}

function filter<K, v>(map: Map<K, v>, filterFunction: (key: K, value: v) => boolean): Map<K, v> {
    const filteredMap: Map<K, v> = new Map<K, v>();

    map.forEach((value, key) => {
        if (filterFunction(key, value)) {
            filteredMap.set(key, value);
        }
    });

    return filteredMap;
}

export const filterBy = (opt: Map<string, ContactPersonDetailRes>, value: string): Map<string, ContactPersonDetailRes> => {
    const filterValue = value.toLowerCase();
    return filter(opt, (key, _) => key.toLowerCase().includes(filterValue))
};


@Component({
    selector: 'app-quick-documentation-dialog',
    templateUrl: './quick-documentation.component.html',
    styleUrls: ['./quick-documentation.component.scss'],
    standalone: false
})
export class QuickDocumentationComponent implements OnInit {

    uploadedFiles: FileUploadRes[] = []
    rawCompanies: CompanyGrouping[] = [];
    filteredCompanies: Observable<CompanyGrouping[]> = new Observable<CompanyGrouping[]>();

    rawContact: ContactGrouping[] = []
    filteredContacts = new Observable<ContactGrouping[]>();


    quickGroup = new FormGroup({
        company: new FormControl<string | CompanyDetailsRes>(''),
        lastName: new FormControl<string | ContactPersonDetailRes>(''),
        date: new FormControl<Date>(new Date(Date.now())),
        type: new FormControl<number>(1),
        pronoun: new FormControl<number>(2),
        // @ts-ignore
        phone: new FormControl('', getModularValidator(PatternType.NumbersOnly)),
        // @ts-ignore
        mail: new FormControl('', getModularValidator(PatternType.Email)),
        title: new FormControl('', Validators.required),
        text: new FormControl('')
    });

    pronouns = Salutation;
    types = Commtype;

    constructor(
        @Inject(L10N_LOCALE) public locale: L10nLocale,
        @Inject(MAT_DIALOG_DATA) public data: { contact: ContactPersonDetailRes, companyId: number },
        public dialogRef: MatDialogRef<QuickDocumentationComponent>,
        private restApi: RestApiService,
        private runtimeCache: RuntimeCacheService,
    ) {

        this.restApi.v0.company.search().subscribe(x => {
            for (let comp of x.data) {
                this.rawCompanies.push({name: comp.name, company: comp})


                let helperMap = new Map<string, ContactPersonDetailRes>
                for (let contact of comp.contactPerson) {
                    helperMap.set((contact.lastName + (contact.firstName ? (", " + contact.firstName) : "")), contact)
                }
                this.rawContact.push({company: comp.name, contacts: helperMap})
            }
        })
    }

    ngOnInit(): void {
        this.filteredCompanies = this.quickGroup.get('company')!.valueChanges.pipe(startWith(''),
            map(value => this._companyFilter(value || '')));

        this.filteredContacts = this.quickGroup.get('lastName')!.valueChanges.pipe(startWith(''),
            map(value => {
                const name = typeof value === 'string' ? value : value?.lastName;
                return this._filterGroup(name as string || "");
            }));


        this.quickGroup.get('lastName')!.valueChanges.subscribe(value => {
            if (typeof value !== 'string' && value !== null) {

                this.quickGroup.get('pronoun')!.setValue(value.salutations)
                this.quickGroup.get("phone")?.setValue(value.phone)
                this.quickGroup.get("mail")?.setValue(value.email)
                if (this.quickGroup.get('company')!.value == "")
                    this.quickGroup.get('company')!.setValue(this.rawCompanies.find(value1 => value1.company.contactPerson.includes(value))?.company!)
            }
        })

    }

    private _companyFilter(value: string | CompanyDetailsRes): CompanyGrouping[] {
        if (value) {
            if (typeof value != 'string') return [];
            const filterValue = value.toLowerCase();
            return this.rawCompanies.filter(option => option.name.toLowerCase().includes(filterValue));
        }
        return this.rawCompanies
    }

    private _filterGroup(value: string): ContactGrouping[] {
        if (value)
            return this.rawContact
                .map(group => ({company: group.company, contacts: filterBy(group.contacts, value)}))
                .filter(group =>
                    !(typeof this.quickGroup.get('company')!.value === 'string') ?
                        group.company.includes((this.quickGroup.get('company')!.value as CompanyDetailsRes).name.toLowerCase()) &&
                        group.contacts.size > 0 : null)
        return this.rawContact;
    }

    companyDisplayFn(company: CompanyDetailsRes): string {
        return company && company.name ? company.name : '';
    }

    contactDisplayFn(contact: ContactPersonDetailRes): string {
        return contact && contact.firstName ? (contact.lastName + ", " + contact.firstName) : '';
    }

    async saveQuickInput() {
        let company: CompanyDetailsRes;
        let contact: ContactPersonDetailRes;
        let note = new NoteCreateReq();
        if (typeof this.quickGroup.get('company')!.value !== 'string') {
            company = this.quickGroup.get('company')!.value as CompanyDetailsRes;
        } else {
            let createReq = new CompanyCreateReq(new CompanyDetailsRes())
            createReq.name = this.quickGroup.get('company')!.value as string
            company = await lastValueFrom(this.restApi.v0.company.create(createReq));
        }

        if (typeof this.quickGroup.get('lastName')!.value !== 'string') {
            contact = this.quickGroup.get('lastName')!.value as ContactPersonDetailRes;
        } else {
            let createReq = new ContactPersonCreateReq()
            createReq.companyId = company.id
            createReq.salutations = this.quickGroup.get('pronoun')!.value as number;
            createReq.lastName = this.quickGroup.get('lastName')!.value as string;
            createReq.email = this.quickGroup.get("mail")?.value as string;
            createReq.phone = this.quickGroup.get("phone")?.value as string;
            contact = await lastValueFrom((this.restApi.v0.crm.contact.createContact(createReq)))
        }

        note.title = this.quickGroup.get("title")?.value as string;
        note.text = this.quickGroup.get("text")?.value as string;
        note.type = this.quickGroup.get('type')!.value as number;
        note.communicationPartnerId = contact.id;
        note.companyId = company.id;
        note.communicatedOn = this.quickGroup.get('date')!.value?.toDateString()!;

        this.restApi.v0.crm.note.create(note).subscribe(() => {
            this.dialogRef.close(company.id)
        });
    }

    uploadAttachment(event: FileContainerReq[]) {
        this.restApi.v0.file.uploadFiles(event).subscribe(value => this.uploadedFiles.push(...value.data))
    }

    removeUpload(item: FileUploadRes) {
        this.uploadedFiles.splice(this.uploadedFiles.findIndex(x => x.id === item.id), 1)
    }

    showCheck(controlName: string): CheckStatus {

        switch (typeof this.quickGroup.get(controlName)!.value) {
            case 'string':
                if (this.quickGroup.get(controlName)!.value.length === 0) {
                    return ''
                } else {
                    return 'add'
                }
            case 'object':
                return 'check';
            default:
                return ''
        }
    }
}
